import React, { createContext, useContext, useEffect } from 'react';
import { commitLocalUpdate, graphql, useFragment, useRelayEnvironment } from 'react-relay';
import { usePreviousValue } from 'view/common/use-previous-value';
import type {
    alignmentSettingsContextFragment$data,
    alignmentSettingsContextFragment$key,
} from './__generated__/alignmentSettingsContextFragment.graphql';
import type { alignmentSettingsContextHookFragment$key } from './__generated__/alignmentSettingsContextHookFragment.graphql';

export interface AlignmentSettings {
    horizontalAlignment?: NonNullable<alignmentSettingsContextFragment$data['alignment']>['horizontalAlignment'];
    verticalAlignment?: NonNullable<alignmentSettingsContextFragment$data['alignment']>['verticalAlignment'];
}

export const AlignmentSettingsContext = createContext<AlignmentSettings>({});

const AlignmentSettings: React.FC<
    React.PropsWithChildren<{
        visualConfig: alignmentSettingsContextFragment$key | null;
    }>
> = ({ visualConfig, children }) => {
    const data = useFragment(
        graphql`
            fragment alignmentSettingsContextFragment on HelpLayoutVisualConfig {
                alignment {
                    __id
                    horizontalAlignment
                    verticalAlignment
                }
            }
        `,
        visualConfig
    );

    const alignmentSettings = data?.alignment;

    const parentAlignmentSettings = useAlignmentSettingsContext();
    const prevParentAlignmentSettings = usePreviousValue(parentAlignmentSettings);
    const environment = useRelayEnvironment();

    useEffect(() => {
        if (
            prevParentAlignmentSettings &&
            prevParentAlignmentSettings.horizontalAlignment &&
            prevParentAlignmentSettings?.horizontalAlignment !== parentAlignmentSettings?.horizontalAlignment
        ) {
            commitLocalUpdate(environment, (store) => {
                const id = data?.alignment?.__id;
                if (id) {
                    const alignmentRecord = store.get(id);

                    if (alignmentRecord) {
                        alignmentRecord.setValue(null, 'horizontalAlignment');
                    }
                }
            });
        }
    }, [
        data?.alignment?.__id,
        environment,
        parentAlignmentSettings?.horizontalAlignment,
        prevParentAlignmentSettings,
        prevParentAlignmentSettings?.horizontalAlignment,
    ]);

    if (alignmentSettings) {
        return (
            <AlignmentSettingsContext.Provider value={alignmentSettings}>{children}</AlignmentSettingsContext.Provider>
        );
    }

    return <>{React.Children.only(children)}</>;
};

export const useAlignmentSettingsContext = () => {
    return useContext(AlignmentSettingsContext);
};

export const useAlignmentSettings = (visualConfig: alignmentSettingsContextHookFragment$key | null) => {
    const data = useFragment(
        graphql`
            fragment alignmentSettingsContextHookFragment on HelpLayoutVisualConfig {
                alignment {
                    __id
                    horizontalAlignment
                    verticalAlignment
                }
            }
        `,
        visualConfig
    );
    const alignment = data?.alignment ?? null;

    return {
        horizontalAlignment: alignment?.horizontalAlignment,
        verticalAlignment: alignment?.verticalAlignment,
    };
};

export default AlignmentSettings;
